import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-widest text-slate-600 font-medium">{label}</Label>
      {children}
    </div>
  );
}

export default function MaterialsMaster() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", unit: "", description: "" });
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => setRows((r => Array.isArray(r) ? r : [])((await api.get("/materials-master")).data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/materials-master", form);
      setForm({ name: "", unit: "", description: "" });
      toast.success("Material added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/materials-master/${confirmId}`);
      toast.success("Material deleted");
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); setConfirmId(null); }
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold mb-1">Master</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Materials</h1>
        <p className="text-slate-500 text-sm mt-2">Save materials here and pick them from the dropdown when creating a job card. New materials typed in job cards are auto-saved here.</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 border-slate-200 lg:col-span-2">
          <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>All Materials</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Unit</th><th className="py-2 pr-3">Description</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-500">No materials yet. Add one or create a job card with materials.</td></tr>}
              {rows.map((m) => (
                <tr key={m.id} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-medium">{m.name}</td>
                  <td className="py-2 pr-3 text-slate-600">{m.unit || "—"}</td>
                  <td className="py-2 pr-3 text-slate-500 text-xs">{m.description || "—"}</td>
                  <td className="py-2 text-right"><Button variant="ghost" size="icon" onClick={() => setConfirmId(m.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Add Material</div>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Art Paper 90 GSM" required /></Field>
            <Field label="Unit"><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="e.g. kg, sheets, rolls" /></Field>
            <Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional notes" /></Field>
            <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800"><Plus className="w-4 h-4 mr-2" /> Add</Button>
          </form>
        </Card>
      </div>
      <ConfirmDialog
        open={!!confirmId}
        title="Delete Material?"
        description="This will permanently delete the material. This cannot be undone."
        confirmLabel="Delete Material"
        busy={deleting}
        onCancel={() => setConfirmId(null)}
        onConfirm={remove}
      />
    </Layout>
  );
}
